<template>
  <div class="lock-screen" role="dialog" aria-modal="true" aria-labelledby="lock-title">
    <form class="lock-card" novalidate @submit.prevent="unlock">
      <img :src="logoImg" alt="" class="lock-card__logo" />
      <UserAvatar :src="user?.photo || ''" :name="user?.nom_complet" :size="84" />
      <h1 id="lock-title" class="lock-card__name">{{ user?.nom_complet }}</h1>
      <p class="lock-card__hint"><i class="fas fa-lock" aria-hidden="true"></i> Application verrouillée. Saisissez votre mot de passe pour reprendre là où vous en étiez.</p>

      <label for="lock-password" class="visually-hidden">Mot de passe</label>
      <PasswordInput id="lock-password" ref="passwordRef" v-model="password" autocomplete="current-password" :invalid="Boolean(error)" />
      <p v-if="error" class="small text-danger mt-2 mb-0" role="alert">{{ error }}</p>

      <button type="submit" class="btn btn-primary w-100 mt-3" :disabled="busy || !password">
        <span v-if="busy" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <i v-else class="fas fa-lock-open" aria-hidden="true"></i>Déverrouiller
      </button>
      <button type="button" class="btn btn-link btn-sm mt-2" :disabled="busy" @click="logout">
        <i class="fas fa-right-from-bracket" aria-hidden="true"></i>Ce n'est pas moi : se déconnecter
      </button>
    </form>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { errorMessage } from "@/api/http.js";
import logoImg from "@/assets/img/logo/logo_Ibacy.png";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import PasswordInput from "@/views/auth/PasswordInput.vue";

/**
 * Écran de verrouillage : la session reste ouverte (page et saisies conservées), mais l'application
 * est masquée et l'API refuse toute requête jusqu'à la saisie du mot de passe.
 */
const emit = defineEmits(["unlocked"]);

const store = useStore();
const router = useRouter();
const user = computed(() => store.state.auth.user);
const password = ref("");
const error = ref("");
const busy = ref(false);
const passwordRef = ref(null);

const focusPassword = () => nextTick(() => passwordRef.value?.$el?.querySelector?.("input")?.focus());

const unlock = async () => {
  if (!password.value) return;
  busy.value = true;
  error.value = "";
  try {
    await store.dispatch("auth/unlock", password.value);
    password.value = "";
    emit("unlocked");
  } catch (e) {
    error.value = e?.response?.status === 429 ? "Trop de tentatives : patientez une minute." : errorMessage(e, "Mot de passe incorrect.");
    password.value = "";
    focusPassword();
  } finally {
    busy.value = false;
  }
};

const logout = async () => {
  busy.value = true;
  await store.dispatch("auth/logout");
  store.commit("license/clearLicense");
  busy.value = false;
  router.replace({ name: "login" });
};

onMounted(focusPassword);
</script>

<style scoped>
.lock-screen {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: hsl(var(--background) / 0.82);
  backdrop-filter: blur(14px);
}
.lock-card {
  width: min(380px, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
  padding: 2rem 1.75rem 1.5rem;
  border: 1px solid hsl(var(--border));
  border-radius: calc(var(--radius) * 2);
  background: hsl(var(--card));
  box-shadow: 0 20px 50px hsl(0 0% 0% / 0.18);
}
.lock-card__logo {
  width: 46px;
  height: 46px;
  object-fit: contain;
  margin-bottom: 0.5rem;
}
.lock-card__name {
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0.5rem 0 0;
}
.lock-card__hint {
  font-size: 0.85rem;
  color: hsl(var(--muted-foreground));
  margin: 0 0 0.75rem;
}
.lock-card :deep(.password-input),
.lock-card :deep(input) {
  width: 100%;
}
</style>
